import React, { useEffect, useState } from 'react'
import { Button, List, message, Tooltip } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useSession } from '@slerp/accounts'
import { useMutation, useQuery } from '@apollo/client'
import {
  TOGGLE_CATEGORY_ARCHIVE_STATE,
  TOGGLE_PRODUCT_ARCHIVE_STATE,
  UPDATE_MERCHANT_ARRANGEMENT_SETTINGS,
  buildProductsCategoriesByCategoryIds
} from '../ProductQueries'
import { toggleCategoryArchiveState } from '../Actions'
import Loading from 'components/Utils/Loading'
import { filterProductsWithSingleCategory } from './utils'
import ErrorMessage from 'components/Utils/ErrorMessage'
import { useMerchant } from 'hooks/useGlobalContext'
import { MerchantCategory } from '@slerp/controls'

const ArchivedCategoriesList = () => {
  const { merchant } = useSession()
  const {
    merchantData: { setting: merchantSettings },
    archivedMerchantCategories: categories,
    refetchCategories
  } = useMerchant()

  const categoryIdsAsParams = categories?.map((category: MerchantCategory) => ({
    category_id: { _eq: category.id }
  }))

  const [restoringCategory, setRestoringCategory] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const query = buildProductsCategoriesByCategoryIds()

  const {
    data: productsCategoriesData,
    loading: isLoadingProductsCategories,
    error: errorProductsCategories
  } = useQuery<{
    products_categories: {
      category_id: string
      product: { id: string; default_variant_id: string }
    }[]
  }>(query, {
    variables: {
      categoryIds: categoryIdsAsParams,
      active: false
    },
    skip: categories.length === 0,
    notifyOnNetworkStatusChange: true,
    onError: (error) =>
      message.error(
        `Unable to load products_categories due to: ${error.message}`,
        5
      )
  })

  const [toggleCategoryArchiveStates] = useMutation(
    TOGGLE_CATEGORY_ARCHIVE_STATE
  )

  const [toggleProductArchiveState] = useMutation(TOGGLE_PRODUCT_ARCHIVE_STATE)

  const [updateMerchantArrangementSettings] = useMutation(
    UPDATE_MERCHANT_ARRANGEMENT_SETTINGS
  )

  useEffect(() => {
    if (categories.length > 0) {
      setRestoringCategory('')
    }
  }, [categories])

  const restoreCategory = (category: MerchantCategory) => {
    const { products_categories } = productsCategoriesData!

    if (!categories)
      return message.error(
        'Unable to restore category due to empty categories data',
        5
      )
    if (!products_categories)
      return message.error(
        'Unable to restore category due to empty products_categories data',
        5
      )

    const productsOnCurrentCategory = products_categories.filter(
      (pc) => pc.category_id === category.id
    )
    const filterProductsCategoriesWithCurrentProducts =
      products_categories.filter((pc) =>
        productsOnCurrentCategory.some(
          (ppc) => ppc.product.id === pc.product.id
        )
      )

    const affectedProducts = filterProductsWithSingleCategory(
      filterProductsCategoriesWithCurrentProducts
    )

    if (merchantSettings) {
      setRestoringCategory(category.id)
      toggleCategoryArchiveState({
        categoryId: category.id,
        shouldArchive: false,
        merchantSlug: merchant?.slug || '',
        merchantSettings,
        toggleArchiveState: toggleCategoryArchiveStates,
        toggleProductArchiveState,
        updateMerchantArrangementSettings,
        affectedProducts
      })
        .then(() => {
          refetchCategories()
          message.destroy()
          message.success(`Category ${category.name} restored!`, 3)
        })
        .catch((error) => {
          message.destroy()
          message.error(
            `Unable to restore category ${category.name} due to: ${error.message}`,
            3
          )
        })
    }
  }

  const renderCategoryRow = (categoryItem: MerchantCategory) => {
    const isRestoring = restoringCategory === categoryItem.id

    return (
      <List.Item
        actions={[
          <Tooltip title='Restore category'>
            <Button
              data-testid={`restore-category-${categoryItem.name
                .replace(/\s+/g, '-')
                .toLowerCase()}`}
              disabled={!!restoringCategory}
              loading={isRestoring}
              size='small'
              icon={<ReloadOutlined />}
              onClick={() => restoreCategory(categoryItem)}
            />
          </Tooltip>
        ]}
      >
        {categoryItem.name}
      </List.Item>
    )
  }

  useEffect(() => {
    setIsLoading(isLoadingProductsCategories)
  }, [isLoadingProductsCategories])

  if (isLoading) return <Loading />
  if (errorProductsCategories)
    return (
      <ErrorMessage
        title='Products categories error'
        subheading={errorProductsCategories?.message}
      />
    )

  return (
    <>
      <>
        <List
          bordered
          dataSource={categories}
          size='small'
          renderItem={renderCategoryRow}
          pagination={{
            defaultCurrent: 1,
            showSizeChanger: true
          }}
        />
      </>
    </>
  )
}

export default ArchivedCategoriesList
